import { useLocation } from 'react-router-dom';
import { MAX_LG_WIDTH, useDeviceDetectorService } from 'framework/utils';

import { useSidebarStorage } from './useSidebarStorage';
import { useSidebarPermissions } from './useSidebarPermissions';
import { useSidebarNavigation } from './useSidebarNavigation';
import { collectUrls, filterItems, updateUrls } from './useSidebarUtils';
import { Navigation, NavigationItemType } from './types';

/**
 * Main hook that combines sidebar state, filtering, and navigation logic.
 */
const useSidebarNav = ({ navigationConfig, exceptions }: { navigationConfig: Navigation; exceptions?: string[] }) => {
  const { pathname } = useLocation();
  const { isDesktop } = useDeviceDetectorService({
    maxWidth: MAX_LG_WIDTH,
    defaultValue: false,
  });

  const { openMenuList, updateOpenMenuList, addToOpenMenuList, getNavWithOpenedActiveMenus } = useSidebarStorage({
    navigationConfig,
    exceptions,
  });
  const { filterNav } = useSidebarPermissions();
  const { getHeaderMenuData } = useSidebarNavigation(pathname, isDesktop);

  /**
   * Combines filtering, URL updates, and open menu state into a final navigation tree.
   */
  const getNavigationItems = () => getNavWithOpenedActiveMenus(updateUrls(filterNav(navigationConfig)));

  /**
   * Collects URLs and filters desktop-only items.
   */
  const getSidebarItems = (items: NavigationItemType[]): NavigationItemType[] => {
    const itemsWithUrls = collectUrls(items);
    return filterItems(itemsWithUrls, isDesktop);
  };

  return {
    openMenuList,
    updateOpenMenuList,
    addToOpenMenuList,
    getNavigationItems,
    getSidebarItems,
    getHeaderMenuData,
  };
};

export default useSidebarNav;
